/*
 * Purchase limits (replaces KOR Order Limit)
 * Reads the custom.purchase_limit product metafield through the purchase-limit-data section and makes sure the
 * cart never holds more of a limited product (all variants combined) than the customer is still allowed:
 *   logged-in customers: limit minus what they have already ordered
 *   guests, when custom.purchase_limit_guest is ticked: the limit (earlier orders are checked by the Flow backstop)
 *   guests otherwise: 0, because the release requires an account
 * Runs after every cart change made by the theme, and once on page load when the cart has items.
 */
(() => {
  if (window.__purchaseLimitsLoaded) return;
  window.__purchaseLimitsLoaded = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);
  const CART_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const STORAGE_KEY = 'purchaseLimitMessage';
  let running = false;
  let pending = false;
  let timer = null;

  function toast(message) {
    if (!message) return;
    let el = document.getElementById('purchase-limit-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'purchase-limit-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483000;max-width:min(92vw,520px);padding:14px 18px;border-radius:8px;background:#1c1c1c;color:#ffffff;font:inherit;font-size:15px;line-height:1.4;box-shadow:0 8px 24px rgba(0,0,0,0.25);display:none';
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.style.display = 'block';
    clearTimeout(el._hide);
    el._hide = setTimeout(() => { el.style.display = 'none'; }, 8000);
  }

  async function getCartLimits() {
    const res = await nativeFetch(root + '?sections=purchase-limit-data', { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' });
    const json = await res.json();
    const doc = new DOMParser().parseFromString(json['purchase-limit-data'] || '', 'text/html');
    const node = doc.getElementById('purchase-limit-data');
    return node ? JSON.parse(node.textContent) : { logged_in: false, items: [] };
  }

  async function refreshCart() {
    try {
      const cart = await (await nativeFetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart: cart } }));
    } catch (e) { /* ignore */ }
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async function enforce() {
    if (running) { pending = true; return false; }
    running = true;
    let changed = false;
    try {
      const data = await getCartLimits();
      const products = {};
      (data.items || []).forEach((item) => {
        if (!item.limit || item.limit < 1) return;
        if (!products[item.product_id]) products[item.product_id] = { title: item.title, limit: item.limit, past: item.past || 0, guestOk: !!item.guest_ok, lines: [] };
        products[item.product_id].lines.push(item);
      });

      const fixes = [];
      const messages = [];
      Object.keys(products).forEach((id) => {
        const p = products[id];
        let allowed;
        if (data.logged_in) allowed = Math.max(0, p.limit - p.past);
        else allowed = p.guestOk ? p.limit : 0;
        const total = p.lines.reduce((sum, l) => sum + l.quantity, 0);
        if (total <= allowed) return;
        let excess = total - allowed;
        for (let i = 0; i < p.lines.length && excess > 0; i++) {
          const line = p.lines[i];
          const cut = Math.min(line.quantity, excess);
          fixes.push({ id: line.key, quantity: line.quantity - cut });
          excess -= cut;
        }
        if (!data.logged_in && !p.guestOk) {
          messages.push(p.title + ' is a limited release. Please log in to your account to buy it.');
        } else if (data.logged_in && p.past > 0) {
          messages.push(p.title + ' is limited to ' + p.limit + ' per customer and you have already ordered ' + p.past + ', so your cart has been updated.');
        } else {
          messages.push(p.title + ' is limited to ' + p.limit + ' per customer, so your cart has been updated.');
        }
      });

      if (!fixes.length) return false;

      for (const fix of fixes) {
        await nativeFetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(fix)
        });
      }
      changed = true;
      const message = messages.join(' ');

      if (/^\/cart\/?$/.test(window.location.pathname.replace(root, '/'))) {
        try { sessionStorage.setItem(STORAGE_KEY, message); } catch (e) { /* ignore */ }
        window.location.reload();
        return true;
      }

      toast(message);
      await refreshCart();
    } catch (e) {
      console.warn('[purchase-limits]', e);
    } finally {
      running = false;
      if (pending) { pending = false; schedule(); }
    }
    return changed;
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(enforce, 250);
  }

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : String((input && input.url) || input || '');
    const request = nativeFetch(input, init);
    if (CART_URL.test(url)) {
      request.then((res) => { if (res.ok) schedule(); }).catch(() => {});
    }
    return request;
  };

  window.PurchaseLimits = { enforce: enforce };

  document.addEventListener('DOMContentLoaded', () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) { sessionStorage.removeItem(STORAGE_KEY); toast(saved); }
    } catch (e) { /* ignore */ }
    if (window.purchaseLimitCartCount > 0) enforce();
  });
})();
